import type { IconSpec } from '@hibro/shared';
import { APP_LINK_DEFAULT_ICON_ID } from '@hibro/shared';
import { Icon } from '@hibro/ui-client';
import { type ReactNode, useEffect, useState } from 'react';

const DEFAULT_SPEC = {
  type: 'builtin',
  id: APP_LINK_DEFAULT_ICON_ID,
} as const;

export type AppLinkIconView =
  | { kind: 'image'; src: string }
  | { kind: 'default'; id: string };

/** Decide what an app-link paints: a URL image, or the default link mark. */
export function appLinkIconView(spec?: IconSpec): AppLinkIconView {
  if (spec?.type === 'resource') {
    const src = spec.light.trim();
    if (src) {
      return { kind: 'image', src };
    }
  }
  return { kind: 'default', id: APP_LINK_DEFAULT_ICON_ID };
}

/**
 * Rail / settings mark for an app-link. A URL paints as an image; missing or
 * broken URLs use the same default link glyph the app-center list uses — never
 * a first letter (`fallbackText`) or the help circle.
 */
export function AppLinkIcon({
  spec,
  size,
  className,
}: {
  spec?: IconSpec;
  size: number;
  className?: string;
}): ReactNode {
  const view = appLinkIconView(spec);
  const src = view.kind === 'image' ? view.src : '';
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (view.kind === 'image' && !failed) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        className={className}
        onError={() => setFailed(true)}
      />
    );
  }

  return <Icon spec={DEFAULT_SPEC} size={size} className={className} />;
}
